'use client';

import { Session } from 'next-auth';
import { SessionProvider } from 'next-auth/react';
import React from 'react';
import MessageBanner from '@/components/molecules/message-banner/message-banner';
import BookingFlowNavigation from '@/components/organisms/booking-flow-navigation/booking-flow-navigation';
import { FooterProps } from '@/components/organisms/footer/footer';
import LoginCurtainWrapper from '@/components/organisms/login-curtain/login-curtain-wrapper';
import { BookingFlowContextProvider } from '@/libraries/contexts/booking-flow-context/booking-flow-context';
import { PageDetailsProvider } from '@/libraries/contexts/page-details-context/page-details-context';
import { useSiteSettingsContext } from '@/libraries/contexts/site-settings-context';
import { UserProfileContextProvider } from '@/libraries/contexts/user-profile-context/user-profile-context';
import { useScrollPersistence } from '@/libraries/hooks/use-scroll-persistence';

export interface SecondaryPageLayoutProps {
  bookingType?: Content.Components['BookingType'];
  brand: Content.Properties.Brand;
  children?: React.ReactNode;
  destinationId?: number;
  enableTransparentNavigation?: boolean;
  footer?: FooterProps;
  hideMainNavigation?: boolean;
  homePageLink?: Content.Properties.Link | null;
  hotelDetails?: Content.PageType.HotelPage['hotelDetails'];
  offerDetails?: Content.PageType.HotelPage['offerDetails'];
  searchWidgetState?: Content.Components['SearchWidgetState'];
  session?: Session | null;
  showTimer?: boolean;
  trackingData: Content.Properties.TrackingData;
  userProfile?: Content.Components['GetUserProfileResponse'];
}

const SecondaryPageLayout = ({
  bookingType,
  brand,
  children,
  destinationId,
  enableTransparentNavigation = false,
  hideMainNavigation = false,
  homePageLink,
  hotelDetails,
  offerDetails,
  searchWidgetState,
  session,
  showTimer = false,
  trackingData,
  userProfile,
}: SecondaryPageLayoutProps) => {
  const { messageBanner } = useSiteSettingsContext();

  useScrollPersistence();

  return (
    <React.Fragment>
      <SessionProvider basePath="/api/auth" session={session}>
        <PageDetailsProvider
          initialData={{
            bookingType,
            brand,
            destinationId,
            hasTransparentNavigation: enableTransparentNavigation,
            hotelDetails,
            offerDetails,
            searchWidgetState,
            trackingData,
          }}
        >
          <BookingFlowContextProvider>
            <UserProfileContextProvider initialData={{ userProfile }}>
              <div data-brand={brand}>
                {!hideMainNavigation && (
                  <BookingFlowNavigation
                    homePageLink={homePageLink}
                    showTimer={showTimer}
                  />
                )}

                {children}

                <LoginCurtainWrapper />
              </div>
            </UserProfileContextProvider>
          </BookingFlowContextProvider>
        </PageDetailsProvider>
      </SessionProvider>

      {messageBanner && (
        <MessageBanner {...messageBanner} enabled={messageBanner.enable} />
      )}
    </React.Fragment>
  );
};

export default SecondaryPageLayout;
